import { tickerApi } from '../services/tickerApi';

export type TickerPageType = 'technicals' | 'fundamentals' | 'analysts' | 'insiders' | 'prosCons' | 'performance';

/**
 * Save user ticker preferences for a specific page type
 */
export async function saveUserTickerPreferences(
  tickerBasicsId: number,
  pageType: TickerPageType,
  hiddenStatistics: string[],
  hiddenDaysAgo?: number[]
): Promise<void> {
  console.log(`🔧 saveUserTickerPreferences called with:`, {
    tickerBasicsId,
    pageType,
    hiddenStatistics,
    hiddenDaysAgo
  });
  
  try {
    // Check if preferences already exist
    let existingPreferences = null;
    try {
      console.log(`🔍 Checking for existing preferences for ticker ${tickerBasicsId}`);
      const response = await tickerApi.getUserTickerPreferences(tickerBasicsId);
      existingPreferences = response.data;
      console.log(`✅ Found existing preferences:`, existingPreferences);
    } catch (error) {
      // Preferences don't exist yet, we'll create them
      console.log('No existing ticker preferences found, will create new ones');
    }

    // Prepare the update data based on page type
    const updateData: any = {};
    switch (pageType) {
      case 'technicals':
        updateData.technicalIndicatorsToHide = hiddenStatistics;
        if (hiddenDaysAgo && hiddenDaysAgo.length > 0) {
          updateData.technicalDaysAgoToHide = hiddenDaysAgo;
        }
        break;
      case 'fundamentals':
        updateData.fundamentalIndicatorsToHide = hiddenStatistics;
        break;
      case 'analysts':
        updateData.analystIndicatorsToHide = hiddenStatistics;
        break;
      case 'insiders':
        updateData.insiderIndicatorsToHide = hiddenStatistics;
        break;
      case 'prosCons':
        updateData.prosConsIndicatorsToHide = hiddenStatistics;
        break;
      case 'performance':
        updateData.performanceIndicatorsToHide = hiddenStatistics;
        break;
    }

    if (existingPreferences) {
      // Update existing preferences
      console.log(`🔄 Updating existing preferences with data:`, updateData);
      await tickerApi.updateUserTickerPreferences(tickerBasicsId, updateData);
      console.log(`✅ Updated ${pageType} ticker preferences for ticker ${tickerBasicsId}`);
    } else {
      // Create new preferences
      console.log(`🆕 Creating new preferences with data:`, { tickerBasicsId, ...updateData });
      await tickerApi.createUserTickerPreferences({
        tickerBasicsId,
        ...updateData
      });
      console.log(`✅ Created ${pageType} ticker preferences for ticker ${tickerBasicsId}`);
    }
  } catch (error) {
    console.error(`❌ Failed to save ${pageType} ticker preferences:`, error);
    throw error;
  }
}

/**
 * Load user ticker preferences for a specific page type
 */
export async function loadUserTickerPreferences(
  tickerBasicsId: number,
  pageType: TickerPageType
): Promise<{ hiddenStatistics: string[]; hiddenDaysAgo?: number[] }> {
  try {
    const response = await tickerApi.getUserTickerPreferences(tickerBasicsId);
    const preferences = response.data;

    switch (pageType) {
      case 'technicals':
        return {
          hiddenStatistics: preferences.technicalIndicatorsToHide || [],
          hiddenDaysAgo: preferences.technicalDaysAgoToHide || []
        };
      case 'fundamentals':
        return {
          hiddenStatistics: preferences.fundamentalIndicatorsToHide || []
        };
      case 'analysts':
        return {
          hiddenStatistics: preferences.analystIndicatorsToHide || []
        };
      case 'insiders':
        return {
          hiddenStatistics: preferences.insiderIndicatorsToHide || []
        };
      case 'prosCons':
        return {
          hiddenStatistics: (preferences as any).prosConsIndicatorsToHide || []
        };
      case 'performance':
        return {
          hiddenStatistics: (preferences as any).performanceIndicatorsToHide || []
        };
      default:
        return { hiddenStatistics: [] };
    }
  } catch (error) {
    console.log(`No ticker preferences found for ${pageType} page`);
    return { hiddenStatistics: [] };
  }
}
